import api from './api';
import {
  LoginRequest,
  LoginResponse,
  RegisterPlayerRequest,
  RegisterCoachRequest,
  RegisterSponsorRequest,
  ApiResponse,
  User,
} from '@types/index';

/**
 * Authentication API service
 */
class AuthService {
  /**
   * Login user
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('AuthService - Sending login request to /login with:', { email: data.email });
      const response = await api.post<ApiResponse<LoginResponse>>('/login', data);
      console.log('AuthService - Login response:', response);
      console.log('AuthService - Response data:', response.data);
      console.log('AuthService - Response data.data:', response.data.data);
      
      // Store tokens and user info
      if (response.data.data) {
        const rawUser = response.data.data.user;
        const isAcademyAdmin = rawUser.role === 'academy_admin' || rawUser.role === 'ACADEMY_ADMIN';
        const isPlatformSuperAdmin = !isAcademyAdmin && (rawUser.isSuperAdmin === true || rawUser.role === 'super_admin');
        const user = {
          ...rawUser,
          isSuperAdmin: isAcademyAdmin ? rawUser.isSuperAdmin === true : isPlatformSuperAdmin,
          role: isPlatformSuperAdmin ? 'super_admin' : rawUser.role,
        };
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken || '');
        localStorage.setItem('user', JSON.stringify(user));
        console.log('AuthService - Tokens stored in localStorage');
        return { ...response.data.data, user };
      } else {
        console.error('AuthService - No data in response:', response.data);
        throw new Error('Invalid response format from server');
      }
    } catch (error: any) {
      console.error('AuthService - Login error:', error);
      console.error('AuthService - Error response:', error.response);
      throw error;
    }
  }

  /**
   * Register player
   */
  async registerPlayer(data: RegisterPlayerRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/auth/register/player',
      data
    );

    const payload: any = response.data.data || {};

    // Child accounts may require parental consent before tokens are issued
    if (response.data.code === 'PARENTAL_CONSENT_PENDING' || payload.parentalConsentPending) {
      return {
        ...payload,
        parentalConsentPending: true,
        user: payload.user ?? null,
        accessToken: payload.accessToken ?? null,
        refreshToken: payload.refreshToken ?? null,
      } as LoginResponse;
    }

    // Store tokens and user info only when issued
    if (payload.accessToken) {
      localStorage.setItem('accessToken', payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken || '');
      if (payload.user) {
        localStorage.setItem('user', JSON.stringify(payload.user));
      }
    }

    return payload;
  }

  /**
   * Register coach
   */
  async registerCoach(data: RegisterCoachRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/auth/register/coach',
      data
    );
    
    const payload = response.data.data;
    if (payload?.accessToken) {
      localStorage.setItem('accessToken', payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken || '');
      if (payload.user) {
        localStorage.setItem('user', JSON.stringify(payload.user));
      }
    }

    return payload!;
  }

  /**
   * Register sponsor partner account
   */
  async registerSponsor(data: RegisterSponsorRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/auth/register/sponsor',
      data
    );

    if (response.data.data) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }

    return response.data.data!;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      // Clear local storage
      this.clearLocalSession();
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/auth/me');
    return response.data.data!;
  }

  /**
   * Clear local session keys (tokens + user). Does not call the API.
   */
  clearLocalSession(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * True only when a usable access token is present (rejects empty / expired JWT / garbage).
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token || token === 'undefined' || token === 'null' || !String(token).trim()) {
      return false;
    }

    // If JWT, reject expired tokens and clear stale session
    const parts = String(token).split('.');
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.exp && Number(payload.exp) * 1000 < Date.now()) {
          this.clearLocalSession();
          return false;
        }
      } catch {
        // Non-decodable payload — still treat non-empty token as present
      }
    }

    return true;
  }

  /**
   * Get stored user info (null if session is inactive).
   */
  getStoredUser(): User | null {
    if (!this.isAuthenticated()) return null;
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr || userStr === 'undefined' || userStr === 'null') return null;
      const parsed = JSON.parse(userStr);
      if (!parsed || typeof parsed !== 'object') return null;
      // Accept id from common shapes
      const id = parsed.id || parsed.userId || parsed.user_id;
      if (!id) return null;
      return { ...parsed, id: String(id) } as User;
    } catch {
      return null;
    }
  }

  /**
   * Active browser session: valid token + stored user with id.
   */
  hasActiveSession(): boolean {
    return this.isAuthenticated() && Boolean(this.getStoredUser()?.id);
  }

  /**
   * Forgot password - request reset link
   */
  async forgotPassword(email: string): Promise<{ message: string; resetLink?: string }> {
    const response = await api.post<ApiResponse<{ message: string; resetLink?: string }>>(
      '/auth/forgot-password',
      { email }
    );
    return response.data.data || { message: response.data.message || '' };
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', { token, newPassword });
  }
}

export default new AuthService();


